import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
  MessageCircle, Send, ArrowLeft, MoreVertical, Archive, 
  Trash2, RefreshCw, Search, Filter, Heart, Users
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { tribalSocialAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import PeacePipeMessage from './PeacePipeMessage';

const UserInbox = ({ className = '', conversationId = null }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleUserProfileClick = (userId, event) => {
    // Stop the event from bubbling up to prevent opening the conversation (only if event is provided)
    if (event) {
      event.stopPropagation();
    }
    if (userId) {
      navigate(`/public-profile/${userId}`);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  // Auto-select conversation if conversationId is provided
  useEffect(() => {
    if (conversationId && conversations.length > 0 && !selectedConversation) {
      const targetConversation = conversations.find(conv => conv.id === conversationId);
      if (targetConversation) {
        setSelectedConversation(targetConversation);
      }
    }
  }, [conversationId, conversations, selectedConversation]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await tribalSocialAPI.getConversations(50);
      setConversations(response.conversations || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load conversations",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      const response = await tribalSocialAPI.getConversationMessages(conversationId, 50, 0);
      setMessages(response.messages || []);
      setSelectedConversation({
        ...response.conversation,
        participant_info: response.participant_info
      });
    } catch (error) {
      console.error('Error loading messages:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load messages",
      });
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;

    const otherParticipant = selectedConversation.participant_info?.find(p => p.user_id !== user?.id);
    if (!otherParticipant) return;

    try {
      setSending(true);
      const result = await tribalSocialAPI.sendMessage(
        otherParticipant.user_id,
        newMessage.trim()
      );

      if (result.success) {
        // Add message to local state immediately
        const newMsg = {
          id: result.message_id,
          sender_id: user.id,
          content: newMessage.trim(),
          message_type: 'text',
          metadata: {},
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');
        
        // Refresh conversations to update last message
        loadConversations();
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send message",
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation || !otherParticipant) return;

    try {
      // Ask for confirmation
      const confirmed = window.confirm(
        `Delete entire conversation with ${otherParticipant.tribal_name || 'Unknown User'}?\n\nThis will permanently delete all messages in this conversation for both participants. This action cannot be undone.`
      );
      if (!confirmed) return;

      // Call API to delete conversation
      const result = await tribalSocialAPI.deleteConversation(selectedConversation.id);
      
      if (result.success) {
        toast({
          title: "Conversation Deleted",
          description: `Conversation with ${otherParticipant.tribal_name || 'Unknown User'} has been permanently deleted`,
        });
        
        // Go back to conversation list
        setSelectedConversation(null);
        
        // Refresh conversations
        loadConversations();
      } else {
        throw new Error(result.message || 'Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete conversation",
      });
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      // Ask for confirmation
      const confirmed = window.confirm('Are you sure you want to delete this message? This action cannot be undone.');
      if (!confirmed) return;

      // Call API to delete message
      const result = await tribalSocialAPI.deleteMessage(messageId);
      
      if (result.success) {
        // Remove message from local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        
        toast({
          title: "Message Deleted",
          description: "Message has been permanently deleted",
        });
        
        // Refresh conversations to update last message if necessary
        loadConversations();
      } else {
        throw new Error(result.message || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete message",
      });
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getMessageIcon = (messageType) => {
    switch (messageType) {
      case 'peace_pipe':
        return '🕊️';
      case 'kinship_request':
        return '🤝';
      case 'ceremony_invite':
        return '🔥';
      default:
        return '💬';
    }
  };

  const filteredConversations = conversations.filter(conv =>
    searchTerm === '' || 
    conv.other_user?.tribal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.last_message?.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Conversation List View
  if (!selectedConversation) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadConversations}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {filteredConversations.length === 0 && !loading ? (
            <div className="text-center py-8">
              <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {conversations.length === 0 ? 'No Messages Yet' : 'No Matching Conversations'}
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                {conversations.length === 0 
                  ? 'Start connecting with community members to begin conversations'
                  : 'Try adjusting your search terms'
                }
              </p>
              {conversations.length === 0 && (
                <PeacePipeMessage
                  receiverId=""
                  receiverName="Community Member"
                  trigger={
                    <Button variant="outline" size="sm">
                      <Heart className="w-4 h-4 mr-2" />
                      Send First Message
                    </Button>
                  }
                />
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.conversation_id}
                  onClick={() => loadMessages(conversation.conversation_id)}
                  className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <Avatar 
                    className="w-12 h-12 cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all"
                    onClick={(e) => handleUserProfileClick(conversation.other_user?.id, e)}
                  >
                    <AvatarImage src={conversation.other_user?.profile_picture} />
                    <AvatarFallback>
                      {conversation.other_user?.tribal_name?.charAt(0)?.toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 
                        className="font-medium text-gray-900 truncate cursor-pointer hover:text-amber-600 transition-colors"
                        onClick={(e) => handleUserProfileClick(conversation.other_user?.id, e)}
                        title={conversation.other_user?.tribal_name || 'Unknown User'}
                      >
                        {conversation.other_user?.tribal_name || 'Unknown User'}
                      </h4>
                      <div className="flex items-center space-x-2">
                        {conversation.last_message?.message_type !== 'text' && (
                          <span className="text-lg">
                            {getMessageIcon(conversation.last_message.message_type)}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {formatMessageTime(conversation.last_message?.timestamp)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-gray-600 truncate">
                        {conversation.last_message?.content || 'No messages yet'}
                      </p>
                      {conversation.unread_count > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Conversation Detail View
  const otherParticipant = selectedConversation.participant_info?.find(p => p.user_id !== user?.id);

  return (
    <Card className={className}>
      <CardHeader className="border-b">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedConversation(null)}
              className="mr-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Avatar 
              className="w-8 h-8 mr-3 cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all"
              onClick={(e) => handleUserProfileClick(otherParticipant?.id, e)}
            >
              <AvatarImage src={otherParticipant?.profile_picture} />
              <AvatarFallback>
                {otherParticipant?.tribal_name?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 
                className="font-medium cursor-pointer hover:text-amber-600 transition-colors"
                onClick={(e) => handleUserProfileClick(otherParticipant?.id, e)}
                title={otherParticipant?.tribal_name || 'Unknown User'}
              >
                {otherParticipant?.tribal_name || 'Unknown User'}
              </h3>
              <p className="text-sm text-gray-500">Conversation</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={handleDeleteConversation}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Conversation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Messages Area */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {messagesLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Start the conversation</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.sender_id === user?.id;
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
                >
                  <div className="flex items-end space-x-1 max-w-xs lg:max-w-md">
                    {/* Delete button - only visible on hover and for the current user's messages */}
                    {isOwnMessage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        onClick={() => handleDeleteMessage(message.id)}
                        title="Delete message"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                    
                    <div
                      className={`px-4 py-2 rounded-lg ${
                        isOwnMessage
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      {message.message_type !== 'text' && (
                        <div className="flex items-center mb-1">
                          <span className="text-lg mr-2">
                            {getMessageIcon(message.message_type)}
                          </span>
                          <Badge 
                            variant={isOwnMessage ? "secondary" : "outline"} 
                            className="text-xs"
                          >
                            {message.metadata?.title || 'Cultural Message'}
                          </Badge>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      <p className={`text-xs mt-1 ${
                        isOwnMessage ? 'text-amber-100' : 'text-gray-500'
                      }`}>
                        {formatMessageTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Message Input */}
        <div className="border-t p-4">
          <div className="flex space-x-2">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 min-h-[40px] max-h-32 resize-none"
              rows={1}
            />
            <div className="flex flex-col space-y-2">
              <Button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
              <PeacePipeMessage
                receiverId={otherParticipant?.user_id || ''}
                receiverName={otherParticipant?.tribal_name || 'Unknown User'}
                trigger={
                  <Button variant="outline" size="sm">
                    🕊️
                  </Button>
                }
                onMessageSent={() => {
                  loadMessages(selectedConversation.id);
                  loadConversations();
                }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserInbox;